import React from 'react';
import CollapsiblePanel from './CollapsiblePanel';
import { Field, inputClass } from './BackgroundFields';
import {
  DEFAULT_GRADIENT_FILL,
  DEFAULT_SOLID_FILL,
  clampAlpha,
  hexForPicker,
  normalizeColorFill,
  normalizeGradientStops,
  suggestGradientStopPosition,
  resolveColorFillCss,
  toRgba,
} from '../utils/colorFillUtils';

function StopEditor({ stop, onChange, onRemove, canRemove }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2 bg-background">
      <input
        type="color"
        className="h-9 w-10 rounded border border-border shrink-0"
        value={hexForPicker(stop.color)}
        onChange={(e) => onChange({ color: e.target.value })}
        aria-label="Couleur"
      />
      <input
        type="range"
        min="0"
        max="100"
        value={Math.round((stop.alpha ?? 1) * 100)}
        onChange={(e) => onChange({ alpha: Number(e.target.value) / 100 })}
        className="flex-1 min-w-[72px]"
        title="Opacité"
      />
      <input
        type="number"
        min="0"
        max="100"
        className="w-14 rounded border border-border px-1.5 py-1 text-xs"
        value={stop.position ?? 0}
        onChange={(e) => onChange({ position: Number(e.target.value) || 0 })}
        title="Position %"
      />
      <span className="text-[10px] text-muted-foreground">%</span>
      {canRemove ? (
        <button
          type="button"
          className="text-xs text-destructive hover:underline"
          onClick={onRemove}
        >
          Retirer
        </button>
      ) : null}
    </div>
  );
}

function ColorFillPreviewBar({ fill, fallback = '#ffffff', variant = 'compact' }) {
  const normalized = normalizeColorFill(fill, fallback);
  const css = resolveColorFillCss(normalized, fallback);
  const isGradient = normalized.mode === 'gradient';

  return (
    <div
      className={`relative rounded-lg border border-border overflow-hidden shrink-0 ${
        variant === 'large'
          ? 'w-full h-16'
          : variant === 'header' && isGradient
            ? 'h-8 w-28'
            : 'h-7 w-12'
      }`}
      title="Aperçu"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(45deg, #d4d4d4 25%, transparent 25%), linear-gradient(-45deg, #d4d4d4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d4d4d4 75%), linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
          backgroundSize: '10px 10px',
          backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0',
        }}
      />
      <div className="absolute inset-0" style={{ background: css }} />
      {isGradient ? (
        <span className="absolute bottom-1.5 right-2 text-[10px] font-medium text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
          {normalized.angle ?? 180}°
        </span>
      ) : null}
    </div>
  );
}

function ColorFillEditor({ fill, fallback, onChange }) {
  const setMode = (mode) => {
    if (mode === 'gradient') {
      onChange({ ...DEFAULT_GRADIENT_FILL });
      return;
    }
    const fromGradient = fill.mode === 'gradient' && fill.stops?.[0];
    onChange({
      mode: 'solid',
      color: fromGradient?.color || fill.color || hexForPicker(fallback),
      alpha: fromGradient?.alpha ?? fill.alpha ?? 1,
    });
  };

  const patchSolid = (patch) => {
    onChange({
      mode: 'solid',
      color: fill.color || hexForPicker(fallback),
      alpha: fill.alpha ?? 1,
      ...patch,
    });
  };

  const patchGradient = (patch) => {
    const base =
      fill.mode === 'gradient'
        ? fill
        : {
            mode: 'gradient',
            angle: DEFAULT_GRADIENT_FILL.angle,
            stops: DEFAULT_GRADIENT_FILL.stops,
          };
    onChange({
      ...base,
      mode: 'gradient',
      ...patch,
      ...(patch.stops ? { stops: normalizeGradientStops(patch.stops) } : {}),
    });
  };

  const updateStop = (index, patch) => {
    const stops = normalizeGradientStops(fill.stops || DEFAULT_GRADIENT_FILL.stops).map(
      (s) => ({ ...s }),
    );
    stops[index] = { ...stops[index], ...patch };
    patchGradient({ stops });
  };

  const addStop = () => {
    const stops = normalizeGradientStops(fill.stops || DEFAULT_GRADIENT_FILL.stops).map(
      (s) => ({ ...s }),
    );
    const { position, color } = suggestGradientStopPosition(stops);
    stops.push({
      id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      color,
      alpha: 1,
      position,
    });
    patchGradient({ stops: normalizeGradientStops(stops) });
  };

  const removeStop = (index) => {
    const stops = normalizeGradientStops(fill.stops || DEFAULT_GRADIENT_FILL.stops).filter(
      (_, i) => i !== index,
    );
    patchGradient({
      stops: stops.length >= 2 ? stops : DEFAULT_GRADIENT_FILL.stops,
    });
  };

  return (
    <>
      <ColorFillPreviewBar
        fill={fill}
        fallback={fallback}
        variant={fill.mode === 'gradient' ? 'large' : 'compact'}
      />

      <div className="flex gap-1 rounded-lg border border-border p-0.5 bg-muted/30">
        {[
          { id: 'solid', label: 'Uni' },
          { id: 'gradient', label: 'Dégradé' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-smooth ${
              fill.mode === opt.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setMode(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {fill.mode === 'solid' ? (
        <>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-10 w-12 rounded-lg border border-border shrink-0"
              value={hexForPicker(fill.color, fallback)}
              onChange={(e) => patchSolid({ color: e.target.value })}
            />
            <input
              className={inputClass}
              value={toRgba(fill.color, fill.alpha ?? 1)}
              onChange={(e) => onChange(parseColorInput(e.target.value, fill))}
              placeholder="rgba(…) ou #rrggbb"
            />
          </div>
          <Field label={`Opacité (${Math.round((fill.alpha ?? 1) * 100)}%)`}>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round((fill.alpha ?? 1) * 100)}
              onChange={(e) => patchSolid({ alpha: clampAlpha(Number(e.target.value) / 100) })}
              className="w-full"
            />
          </Field>
        </>
      ) : (
        <>
          <Field label={`Angle (${fill.angle ?? 180}°)`}>
            <input
              type="range"
              min="0"
              max="360"
              value={fill.angle ?? 180}
              onChange={(e) => patchGradient({ angle: Number(e.target.value) })}
              className="w-full"
            />
          </Field>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Points de couleur</span>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={addStop}
              >
                + Ajouter
              </button>
            </div>
            {normalizeGradientStops(fill.stops || DEFAULT_GRADIENT_FILL.stops).map(
              (stop, index, arr) => (
              <StopEditor
                key={stop.id || `stop-${index}`}
                stop={stop}
                onChange={(patch) => updateStop(index, patch)}
                onRemove={() => removeStop(index)}
                canRemove={arr.length > 2}
              />
            ),
            )}
          </div>
        </>
      )}
    </>
  );
}

export { ColorFillPreviewBar };

export default function ColorFillField({
  label,
  value,
  onChange,
  fallback = '#ffffff',
  nested = false,
  defaultOpen = false,
}) {
  const fill = normalizeColorFill(value, fallback);
  const preview = (
    <ColorFillPreviewBar
      fill={fill}
      fallback={fallback}
      variant={fill.mode === 'gradient' ? 'header' : 'compact'}
    />
  );

  if (nested || !label) {
    return (
      <div className="space-y-3 rounded-lg border border-border/60 p-2.5 bg-background/50">
        <ColorFillEditor fill={fill} fallback={fallback} onChange={onChange} />
      </div>
    );
  }

  return (
    <CollapsiblePanel title={label} defaultOpen={defaultOpen} preview={preview}>
      <ColorFillEditor fill={fill} fallback={fallback} onChange={onChange} />
    </CollapsiblePanel>
  );
}

function parseColorInput(raw, currentFill) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { ...DEFAULT_SOLID_FILL, ...currentFill, mode: 'solid' };
  if (trimmed.startsWith('linear-gradient')) {
    return currentFill;
  }
  const rgba =
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(trimmed);
  if (rgba) {
    const r = Math.round(Number(rgba[1]));
    const g = Math.round(Number(rgba[2]));
    const b = Math.round(Number(rgba[3]));
    const a = rgba[4] != null ? Number(rgba[4]) : 1;
    const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
    return { mode: 'solid', color: hex, alpha: clampAlpha(a) };
  }
  if (/^#/.test(trimmed)) {
    return { mode: 'solid', color: hexForPicker(trimmed), alpha: currentFill.alpha ?? 1 };
  }
  return { ...currentFill, mode: 'solid' };
}
